/* SpawnAnything — capture zones: king of the hill, domination and the campaign goal 'capture' (pure, worker-safe).

   A zone is a circle { x, z, r } on the battlefield. Every tick the living, grounded units of each team inside it are
   counted through the simulation's uniform grid and weighted by mass (clamped to [0.25, 4], so one T-Rex is worth four
   soldiers, not fourteen). The stronger side pushes the zone's progress P towards its end (-1 = blue, +1 = red); with
   equal weight on both sides the zone is contested and frozen. An owned zone is lost when P crosses 0 and taken when
   it reaches the end. Owners score `rate` points per second per zone while nobody out-weighs them there; the first
   team to `target` points wins. Annihilation still decides the battle on its own (the worker checks it first).

   Determinism: only + - * / sqrt, floor, min, max and abs; fixed iteration order; Float64Array state.

   Use from the worker (see sim.worker.js, `// feature:zone`):
     ZN = zoneSetup(spec, prevState, env)   once per setup; null = annihilation only
     zoneStep(ZN, dt)                       once per tick after the units moved; returns the winner (-2 = nobody yet)
     zoneSteer(ZN, i, t, out)               AI: unit i without a nearby enemy walks to a zone its team needs
     zoneFrame(ZN, gen)                     plain message for the renderer ({ type: 'zone', n: 0 } when off)

   Use from content (campaign):
     zoneSpecFromObjectives(mission.objectives, team)  →  spec for setup's `zones` field (objective kind 'capture')

   Spec (the `zones` field of the setup message; validated here because it may come from another client):
     { mode: 'vernichtung' | 'koenig' | 'herrschaft' | 'capture',
       target?: points to win (default per mode), rate?: points per second per zone (1),
       zones?: [{ x, z, r? }] (omitted = placed automatically), scorers?: bit mask of teams that can win by points (3),
       capture?: seconds from neutral to taken with a small majority (6) } */
import { cellAt, cellCenter, heightAt, isWalkableCell } from '../../world/terrain.js';
import { computeFlowField, sampleFlow, UNREACHABLE } from '../flowfield.js';

export const ZONE_MODES = ['vernichtung', 'koenig', 'herrschaft', 'capture'];
/** Per mode: default points to win, number of auto-placed zones, default radius (m). */
export const ZONE_DEFAULTS = {
  koenig: { target: 60, n: 1, r: 13 },
  herrschaft: { target: 150, n: 3, r: 11 },
  capture: { target: 60, n: 1, r: 12 },
};
export const ZONE_MAX = 5;
/** LEAD values besides a team index: nobody inside / equal weight on both sides. */
export const ZONE_NONE = -1, ZONE_TIE = -2;
const CAP_S = 6;    // seconds from neutral to taken with a small majority (a big one is up to twice as fast)
const NEAR = 18;    // metres beyond weapon reach: an enemy closer than this is fought instead of walking to a zone
const GUARD = 7;    // every GUARD-th unit holds a zone its team already owns when no enemy is near

const fin = v => typeof v === 'number' && Number.isFinite(v);
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;

/** Validate a spec; returns a normalized copy or null (annihilation only). Idempotent. */
export function zoneSpec(spec) {
  if (!spec || typeof spec !== 'object' || !ZONE_MODES.includes(spec.mode) || spec.mode === 'vernichtung') return null;
  const D = ZONE_DEFAULTS[spec.mode];
  let zones = null;
  if (Array.isArray(spec.zones)) {
    zones = [];
    for (const z of spec.zones) {
      if (zones.length >= ZONE_MAX) break;
      if (!z || !fin(z.x) || !fin(z.z)) continue;
      zones.push({ x: clamp(z.x, -5000, 5000), z: clamp(z.z, -5000, 5000), r: fin(z.r) ? clamp(z.r, 3, 60) : D.r });
    }
    if (!zones.length) zones = null;
  }
  return {
    mode: spec.mode,
    target: fin(spec.target) ? clamp(spec.target, 1, 100000) : D.target,
    rate: fin(spec.rate) ? clamp(spec.rate, 0.01, 100) : 1,
    zones,
    scorers: spec.scorers === 1 || spec.scorers === 2 ? spec.scorers : 3,
    capture: fin(spec.capture) ? clamp(spec.capture, 0.5, 120) : CAP_S,
  };
}

/** Campaign: objectives of kind 'capture' ({ at: [x, z], radius, seconds }) → spec; only `team` wins by holding. */
export function zoneSpecFromObjectives(objectives, team = 0) {
  const caps = (objectives || []).filter(o => o && o.kind === 'capture' && Array.isArray(o.at));
  if (!caps.length) return null;
  let target = 0;
  for (const o of caps) if (o.seconds > target) target = o.seconds;
  return zoneSpec({ mode: 'capture', target: target || ZONE_DEFAULTS.capture.target, scorers: team ? 2 : 1,
    zones: caps.map(o => ({ x: o.at[0], z: o.at[1], r: o.radius })) });
}

/* Best spot in a window around (x, z): at most Ra metres along the axis between the armies (u = unit vector; keeps the
   zone fair, in the middle) and Rc metres across it. High ground first, then room for the zone (same level, dry, no
   ramp), then closeness. Writes the cell centre to out; keeps (x, z) when nothing is walkable. */
function highSpot(t, x, z, ux, uz, Ra, Rc, r, out) {
  const n = t.n, cs = t.cell, c0 = cellAt(t, x, z), gx0 = c0 % n, gz0 = (c0 / n) | 0;
  const k = Math.ceil(Math.max(Ra, Rc) / cs), k2 = Math.max(1, Math.round(r * 0.6 / cs));
  let best = -1, bs = -1e18;
  for (let dz = -k; dz <= k; dz++) for (let dx = -k; dx <= k; dx++) {
    const gx = gx0 + dx, gz = gz0 + dz;
    if (gx < 0 || gz < 0 || gx >= n || gz >= n) continue;
    const da = Math.abs((dx * ux + dz * uz) * cs), dc = Math.abs((dz * ux - dx * uz) * cs);
    if (da > Ra || dc > Rc) continue;
    const i = gz * n + gx; if (!isWalkableCell(t, i) || t.rampDir[i] >= 0) continue;
    const L = t.level[i]; let same = 0, tot = 0;
    for (let sz = -k2; sz <= k2; sz += 2) for (let sx = -k2; sx <= k2; sx += 2) {
      const ax = gx + sx, az = gz + sz; tot++;
      if (ax < 0 || az < 0 || ax >= n || az >= n) continue;
      const j = az * n + ax; if (t.level[j] === L && isWalkableCell(t, j)) same++;
    }
    const s = L * 10 + same / tot * 3 - da * 0.15 - dc * 0.05; // a higher terrace always wins inside the window
    if (s > bs) { bs = s; best = i; }
  }
  if (best >= 0) cellCenter(t, best, out); else { out[0] = x; out[1] = z; }
  return out;
}

/**
 * Automatic layout between the two armies' centres c0, c1: 'koenig' / 'capture' one zone on the highest ground in a band
 * across the middle (equally far from both armies), 'herrschaft' three zones across the front (A, B, C). Deterministic (setup data only).
 * o = { terrain|null, c0: [x, z], c1: [x, z], gap, extZ, unitR }
 */
export function autoZones(mode, o) {
  const D = ZONE_DEFAULTS[mode] || ZONE_DEFAULTS.koenig, r = Math.max(D.r, o.unitR * 4);
  let ux = o.c1[0] - o.c0[0], uz = o.c1[1] - o.c0[1];
  const ul = Math.sqrt(ux * ux + uz * uz);
  if (ul < 1e-3) { ux = 1; uz = 0; } else { ux /= ul; uz /= ul; }
  const mx = (o.c0[0] + o.c1[0]) / 2, mz = (o.c0[1] + o.c1[1]) / 2, P = [0, 0], out = [];
  const Ra = clamp(o.gap * 0.15, 4, 16);
  const put = (x, z, Rc) => { if (o.terrain) highSpot(o.terrain, x, z, ux, uz, Ra, Rc, r, P); else { P[0] = x; P[1] = z; } out.push({ x: P[0], z: P[1], r }); };
  if (D.n === 1) put(mx, mz, clamp(o.gap * 0.5 + 10, 20, 70));
  else {
    const d = clamp(Math.max(o.extZ * 0.7, o.gap * 0.6), r * 2.4, 70);
    for (let s = -1; s <= 1; s++) put(mx - uz * d * s, mz + ux * d * s, 12);
  }
  return out;
}

const sameZones = (a, b) => a.length === b.length && a.every((z, k) => z.x === b[k].x && z.z === b[k].z && z.r === b[k].r);

/**
 * Build the zone state for a new battle. Returns null when the spec is off.
 * env = { X, Z, ALIVE, AIR, gStart, gItems, C, W, GW, TR, TP, army, gap, extZ } (the worker's arrays after setup;
 * army[t] = { start, resStart, end, mass, r }). prev = the previous state: its flow fields are reused when terrain and
 * zones are unchanged (painting re-sends the setup many times per second).
 */
export function zoneSetup(spec, prev, env) {
  const s = zoneSpec(spec); if (!s) return null;
  const { X, Z, ALIVE, TR, TP, army } = env;
  // army centres from the starting units (reserved spawn slots are dead)
  const c = [[0, 0], [0, 0]];
  for (let t = 0; t < 2; t++) {
    const A = army[t]; let sx = 0, sz = 0, k = 0;
    for (let i = A.start; i < A.resStart; i++) if (ALIVE[i]) { sx += X[i]; sz += Z[i]; k++; }
    if (k) { c[t][0] = sx / k; c[t][1] = sz / k; } else c[t][0] = (t ? 1 : -1) * env.gap / 2;
  }
  let L = env.W - 2; if (TR && TR.n * TR.cell / 2 - 1 < L) L = TR.n * TR.cell / 2 - 1;
  const list = (s.zones || autoZones(s.mode, { terrain: TR, c0: c[0], c1: c[1], gap: env.gap, extZ: env.extZ, unitR: Math.max(army[0].r, army[1].r) }))
    .map(z => { const m = Math.max(0, L - z.r); return { x: clamp(z.x, -m, m), z: clamp(z.z, -m, m), r: z.r }; });
  const n = list.length;
  const S = {
    spec: s, mode: s.mode, target: s.target, rate: s.rate, scorers: s.scorers, cap: 1 / s.capture, near: NEAR, n, list,
    x: new Float64Array(n), z: new Float64Array(n), r: new Float64Array(n), y: new Float64Array(n),
    P: new Float64Array(n), O: new Int8Array(n).fill(-1), LEAD: new Int8Array(n).fill(ZONE_NONE), CONT: new Uint8Array(n),
    WT: new Float64Array(n * 2), need: new Uint8Array(n * 2).fill(1), score: new Float64Array(2), won: -2,
    wt: [clamp(army[0].mass, 0.25, 4), clamp(army[1].mass, 0.25, 4)],
    v: env, tr: TR, tp: TP, fields: null,
  };
  for (let k = 0; k < n; k++) { S.x[k] = list[k].x; S.z[k] = list[k].z; S.r[k] = list[k].r; S.y[k] = TR ? heightAt(TR, list[k].x, list[k].z) : 0; }
  if (TR && TP) {
    if (prev && prev.tp === TP && prev.fields && sameZones(prev.list, list)) S.fields = prev.fields;
    else S.fields = list.map(z => zoneField(TR, TP, z));
  }
  return S;
}

// flow field towards the inner half of a zone (walkable cells within r/2; at least the nearest walkable one)
function zoneField(t, pass, zn) {
  const n = t.n, cs = t.cell, c0 = cellAt(t, zn.x, zn.z), gx0 = c0 % n, gz0 = (c0 / n) | 0, R = zn.r * 0.5, k = Math.ceil(R / cs) + 1;
  const src = [], P = [0, 0];
  let near = -1, nd = 1e18;
  for (let dz = -k; dz <= k; dz++) for (let dx = -k; dx <= k; dx++) {
    const gx = gx0 + dx, gz = gz0 + dz; if (gx < 0 || gz < 0 || gx >= n || gz >= n) continue;
    const i = gz * n + gx; if (!pass.walk[i]) continue;
    cellCenter(t, i, P); const ddx = P[0] - zn.x, ddz = P[1] - zn.z, d2 = ddx * ddx + ddz * ddz;
    if (d2 <= R * R) src.push(i);
    if (d2 < nd) { nd = d2; near = i; }
  }
  if (!src.length && near >= 0) src.push(near);
  return src.length ? computeFlowField(pass, Int32Array.from(src)) : null;
}

/** Weighted unit count per zone and team through the grid (cells overlapping the circle, one cell of slack). */
function countZones(S) {
  const v = S.v, X = v.X, Z = v.Z, ALIVE = v.ALIVE, AIR = v.AIR, C = v.C, W = v.W, GW = v.GW;
  for (let k = 0; k < S.n; k++) {
    const x0 = S.x[k], z0 = S.z[k], r = S.r[k], r2 = r * r;
    const gxa = clamp(Math.floor((x0 - r + W) / C) - 1, 0, GW - 1), gxb = clamp(Math.floor((x0 + r + W) / C) + 1, 0, GW - 1);
    const gza = clamp(Math.floor((z0 - r + W) / C) - 1, 0, GW - 1), gzb = clamp(Math.floor((z0 + r + W) / C) + 1, 0, GW - 1);
    for (let t = 0; t < 2; t++) {
      const st = v.gStart[t], it = v.gItems[t]; let cnt = 0, w = 0;
      for (let gz = gza; gz <= gzb; gz++) for (let gx = gxa; gx <= gxb; gx++) {
        const c = gz * GW + gx;
        for (let q = st[c], e = st[c + 1]; q < e; q++) {
          const j = it[q]; if (!ALIVE[j] || AIR[j]) continue;
          const dx = X[j] - x0, dz = Z[j] - z0;
          if (dx * dx + dz * dz < r2) { cnt++; if (v.mix) w += clamp(v.unitOf(j).mass, 0.25, 4); }
        }
      }
      S.WT[k * 2 + t] = v.mix ? w : cnt * S.wt[t]; // mixed armies: every unit weighs its own troop's mass
    }
  }
}

/** Advance progress, owners and scores by dt from the weights in S.WT (exported for tests). Returns S.won. */
export function zoneUpdate(S, dt) {
  for (let k = 0; k < S.n; k++) {
    const w0 = S.WT[k * 2], w1 = S.WT[k * 2 + 1];
    const lead = w0 > w1 ? 0 : w1 > w0 ? 1 : w0 > 0 ? ZONE_TIE : ZONE_NONE;
    let p = S.P[k], o = S.O[k];
    if (lead >= 0) {
      const adv = w0 > w1 ? w0 - w1 : w1 - w0, sp = S.cap * Math.min(2, 0.75 + adv / 8) * dt;
      p += lead ? sp : -sp;
    } else if (lead === ZONE_NONE) {
      // nobody there: an owned zone recovers, a neutral one drifts back to neutral
      const goal = o < 0 ? 0 : o ? 1 : -1, sp = S.cap * (o < 0 ? 0.25 : 0.5) * dt;
      p = p < goal ? Math.min(goal, p + sp) : Math.max(goal, p - sp);
    }
    p = clamp(p, -1, 1);
    if ((o === 0 && p >= 0) || (o === 1 && p <= 0)) o = -1;
    if (p <= -1) o = 0; else if (p >= 1) o = 1;
    S.P[k] = p; S.O[k] = o; S.LEAD[k] = lead; S.CONT[k] = w0 > 0 && w1 > 0 ? 1 : 0;
    if (o >= 0 && (lead === o || lead === ZONE_NONE) && ((S.scorers >> o) & 1)) S.score[o] += S.rate * dt;
    for (let t = 0; t < 2; t++) S.need[k * 2 + t] = o !== t || S.CONT[k] || lead === 1 - t ? 1 : 0;
  }
  if (S.won === -2) {
    const a = S.score[0] >= S.target, b = S.score[1] >= S.target;
    if (a || b) S.won = a && b ? (S.score[0] > S.score[1] ? 0 : S.score[1] > S.score[0] ? 1 : -1) : a ? 0 : 1;
  }
  return S.won;
}

/** One tick: count (grid must be current) and update. Returns the winner by points (-2 = none yet, -1 = draw). */
export function zoneStep(S, dt) {
  countZones(S);
  return zoneUpdate(S, dt);
}

/**
 * AI steering for unit i of team t that has no enemy within reach + S.near: walk to the nearest zone the team needs
 * (not owned, contested or being taken; by path length on terrain, units spread over the zones by index); every
 * GUARD-th unit also holds owned zones. Writes a unit direction to out.
 * Returns 0 = no zone business (fight as usual), 1 = walk along out, 2 = arrived (out = 0, 0: stay and hold).
 */
export function zoneSteer(S, i, t, out) {
  const v = S.v, x = v.X[i], z = v.Z[i], guard = i % GUARD === 0, tr = S.tr, cell = S.fields ? cellAt(tr, x, z) : 0;
  let best = -1, bd = 1e18;
  for (let pass = 0; pass < 2 && best < 0; pass++) {
    if (pass === 1 && !guard) break;
    for (let k = 0; k < S.n; k++) {
      if (pass === 0 && !S.need[k * 2 + t]) continue;
      const dx = S.x[k] - x, dz = S.z[k] - z, d2 = dx * dx + dz * dz, rr = S.r[k] * 0.55;
      if (d2 < rr * rr) { out[0] = 0; out[1] = 0; return 2; }
      let d = Math.sqrt(d2);
      const f = S.fields && S.fields[k];
      if (f) { const pd = f.dist[cell]; if (pd !== UNREACHABLE) d = pd * tr.cell / 10; }
      if (k !== i % S.n) d *= 1.6; // spread out: each unit leans to one zone unless another is much closer
      if (d < bd) { bd = d; best = k; }
    }
  }
  if (best < 0) return 0;
  const f = S.fields && S.fields[best];
  if (f) {
    const pd = f.dist[cell];
    if (pd === 0) { out[0] = 0; out[1] = 0; return 2; }
    if (pd !== UNREACHABLE && sampleFlow(f, tr, x, z, out)) return 1;
  }
  const dx = S.x[best] - x, dz = S.z[best] - z, d = Math.sqrt(dx * dx + dz * dz) || 1;
  out[0] = dx / d; out[1] = dz / d;
  return 1;
}

/** Renderer message: positions, progress (-1 blue … +1 red), owner, lead, contested, scores. */
export function zoneFrame(S, gen) {
  if (!S) return { type: 'zone', gen, n: 0 };
  return {
    type: 'zone', gen, n: S.n, mode: S.mode, target: S.target, scorers: S.scorers, won: S.won,
    x: Array.from(S.x), z: Array.from(S.z), r: Array.from(S.r), y: Array.from(S.y),
    p: Array.from(S.P), o: Array.from(S.O), lead: Array.from(S.LEAD), cont: Array.from(S.CONT),
    score: [Math.min(S.score[0], S.target), Math.min(S.score[1], S.target)],
  };
}
